import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { CreditCard, PlugZap } from "lucide-react";

export const PaymentSettingsCard = () => {
  const [status, setStatus] = useState(null);
  const [keyId, setKeyId] = useState("");
  const [secret, setSecret] = useState("");
  const [webhook, setWebhook] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/payment-settings").then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/payment-settings", {
        key_id: keyId, key_secret: secret, webhook_secret: webhook,
      });
      toast.success(`Saved: ${(data.changed || ["keys"]).join(", ")}`);
      setSecret(""); setWebhook("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const testConnection = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payment-settings/test");
      toast.success(`Razorpay is live — test order ${data.test_order_id} created`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const togglePause = async (paused) => {
    try {
      await api.put("/admin/payment-settings", { payments_paused: paused });
      toast.success(paused ? "Online payments paused — buyers are sent to WhatsApp" : "Online payments resumed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!status) return null;

  return (
    <Card className="border-border" data-testid="payment-settings-card">
      <CardContent className="p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 mb-1">
          <h3 className="font-display text-2xl flex items-center gap-2"><CreditCard size={18}/> Payment Gateway</h3>
          <Badge
            variant="outline"
            className={status.configured && !status.last_error
              ? "border-primary/40 text-primary bg-primary/10"
              : "border-destructive/40 text-destructive bg-destructive/10"}
            data-testid="payment-status-badge"
          >
            {!status.configured
              ? "Not configured"
              : status.last_error ? "Keys rejected by Razorpay" : `Razorpay live (${status.source})`}
          </Badge>
        </div>
        {status.last_error && (
          <p className="text-sm text-destructive mb-3" data-testid="payment-key-error">
            Razorpay refused the current keys ({status.key_id || "none"}): {status.last_error}. Paste a fresh
            Key ID and Secret below — keys saved here override the server environment.
          </p>
        )}
        <p className="text-sm text-muted-foreground mb-5">
          Paste the keys from your Razorpay dashboard (Account &amp; Settings → API Keys). The secret is stored
          server-side and never shown again.
          {status.env_present && " Keys saved here take priority over the ones in the server environment."}
        </p>

        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <Label className="overline">Key ID</Label>
            <Input value={keyId} onChange={(e) => setKeyId(e.target.value)} placeholder={status.key_id || "rzp_live_xxxxxxxx"} className="mt-2 bg-transparent" data-testid="payment-key-id"/>
          </div>
          <div>
            <Label className="overline">Key Secret {status.secret_last4 && `(saved ····${status.secret_last4})`}</Label>
            <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="write-only" className="mt-2 bg-transparent" data-testid="payment-key-secret"/>
          </div>
          <div className="md:col-span-2">
            <Label className="overline">Webhook Secret ({status.webhook_source})</Label>
            <Input type="password" value={webhook} onChange={(e) => setWebhook(e.target.value)} placeholder="rotate without a redeploy" className="mt-2 bg-transparent" data-testid="payment-webhook-secret"/>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={save} disabled={busy} className="btn-pill bg-primary text-primary-foreground h-10" data-testid="payment-save">
            {busy ? "Saving…" : "Save Keys"}
          </Button>
          <Button variant="outline" onClick={testConnection} disabled={busy} className="btn-pill h-10" data-testid="payment-test">
            <PlugZap size={14} className="mr-1.5"/> Test Connection
          </Button>
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-muted-foreground">Pause online payments</span>
            <Switch checked={status.payments_paused} onCheckedChange={togglePause} data-testid="payment-pause-switch"/>
          </div>
        </div>
        {status.payments_paused && (
          <p className="text-xs text-secondary mt-3" data-testid="payment-paused-note">
            Checkout is showing &quot;payment paused&quot; and directing buyers to WhatsApp ordering.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentSettingsCard;
